/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { useEffect, useRef } from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import { Link } from "react-router-dom";

function Information() {
  const didInitRef = useRef(false);

  useEffect(() => {
    const initMap = () => {
      if (didInitRef.current) return;
      didInitRef.current = true;

      const container = document.getElementById("map");
      if (!container) {
        console.error("[KakaoMap] #map container not found");
        return;
      }
      const { kakao } = window;
      if (!kakao || !kakao.maps) {
        console.error("[KakaoMap] kakao.maps not available");
        return;
      }

      const mapOption = {
        center: new kakao.maps.LatLng(33.450701, 126.570667),
        level: 2,
      };
      const map = new kakao.maps.Map(container, mapOption);

      // Kakao built-in controls (official UI)
      const mapTypeControl = new kakao.maps.MapTypeControl();
      map.addControl(mapTypeControl, kakao.maps.ControlPosition.TOPRIGHT);
      const zoomControl = new kakao.maps.ZoomControl();
      map.addControl(zoomControl, kakao.maps.ControlPosition.RIGHT);

      const geocoder = new kakao.maps.services.Geocoder();

      geocoder.addressSearch("경남 진주시 도동천로 276", function (result, status) {
        if (status === kakao.maps.services.Status.OK) {
          const coords = new kakao.maps.LatLng(result[0].y, result[0].x);

          const marker = new kakao.maps.Marker({
            map: map,
            position: coords,
          });

          const infowindow = new kakao.maps.InfoWindow({
            content: '<div style="width:150px;text-align:center;padding:6px 0;">계명종합중기</div>',
          });
          infowindow.open(map, marker);

          map.setCenter(coords);
        }
      });
    };

    // If already loaded
    if (window.kakao && window.kakao.maps) {
      if (window.kakao.maps.load) {
        window.kakao.maps.load(initMap);
      } else {
        initMap();
      }
      return;
    }

    // Avoid duplicate script tags
    const existing = document.querySelector('script[src*="dapi.kakao.com/v2/maps/sdk.js"]');
    if (existing) {
      existing.addEventListener("load", () => {
        if (window.kakao && window.kakao.maps && window.kakao.maps.load) {
          window.kakao.maps.load(initMap);
        } else {
          initMap();
        }
      });
      return;
    }

    // Inject SDK with autoload=false
    const script = document.createElement("script");
    script.src =
      "https://dapi.kakao.com/v2/maps/sdk.js?appkey=b8be9115b7d35cde6ae5f2831bdb28aa&autoload=false&libraries=services";
    script.async = true;
    script.onload = () => {
      if (window.kakao && window.kakao.maps && window.kakao.maps.load) {
        window.kakao.maps.load(initMap);
      } else {
        initMap();
      }
    };
    script.onerror = () => {
      console.error("[KakaoMap] Failed to load Kakao SDK. Check appkey and allowed domains.");
    };
    document.head.appendChild(script);
  }, []);

  return (
    <MKBox component="section" py={12}>
      <Container>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <MKBox borderRadius="lg" overflow="hidden">
              <div
                id="map"
                style={{ width: "100%", height: "500px", minHeight: "500px", display: "block" }}
              ></div>
              <MKBox
                mt={2}
                p={3}
                borderRadius="md"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.9)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <MKTypography variant="h5" fontWeight="bold" gutterBottom>
                  찾아오시는 길 안내
                </MKTypography>

                <MKTypography variant="body2" color="text" fontWeight="medium">
                  경상남도 진주시에 위치해 있으며, 찾아오실 때 어려움이 있으시면 언제든 전화 주세요.
                </MKTypography>

                <MKBox mt={1.5}>
                  <MKTypography variant="button" color="dark" fontWeight="bold">
                    도로명
                  </MKTypography>
                  <MKTypography variant="body2" color="text" fontWeight="medium">
                    경남 진주시 도동천로 276, 상가동 3층 301호 (하대동, 현대아파트)
                  </MKTypography>
                </MKBox>

                <MKBox mt={0.75}>
                  <MKTypography variant="button" color="dark" fontWeight="bold">
                    우편번호 / 지번
                  </MKTypography>
                  <MKTypography variant="body2" color="text" fontWeight="medium">
                    (우) 52765, 경남 진주시 하대동 101, 상가동 3층 301호 (하대동, 현대아파트)
                  </MKTypography>
                </MKBox>

                <MKBox mt={2} display="flex" flexWrap="wrap" gap={1}>
                  <MKButton
                    component={Link} // ✅ 리액트 라우터용 링크 버튼
                    to="/tel"
                    color="info"
                  >
                    전화하기
                  </MKButton>

                  <MKButton
                    variant="outlined"
                    color="dark"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        "경남 진주시 도동천로 276 현대이파트 상가 3층 301호"
                      )
                    }
                  >
                    도로명주소 복사
                  </MKButton>
                  <MKButton
                    variant="outlined"
                    color="dark"
                    onClick={() => navigator.clipboard.writeText("경남 진주시 하대동 101")}
                  >
                    지번주소 복사
                  </MKButton>
                </MKBox>
              </MKBox>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Information;
